import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import omit from 'lodash/omit';

import {SESSION_EVENTS} from 'common/report/constants';
import {EMPTY_EVENT_ENTRY} from 'common/widgets/query-events-section/types';
import PropertyFilter from 'common/widgets/property-filter-menu/models/property-filter';
import {
  ResourceType,
  PropertyType,
  ListOperator,
  LogicalOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {getSegmentType, SavedCohortSegment} from 'common/widgets/query-block/segment-menu/types';

import {AnchorsFlowsReport, AnchorsFlowsReportQueryParams} from './types';
import {FlowsReport} from './report';
import FullLengthDateRange from 'common/widgets/date-range-picker/models/full-length-date-range';

/** Converts params of AnchorsFlowsReport to FlowsReport */
export function convertAnchorFlowsToFlowsReport(flowsReport: AnchorsFlowsReport): Partial<FlowsReport> {
  flowsReport = {
    anchors: [],
    dateRange: new FullLengthDateRange(),
    filters: [],
    filterByCohort: null,
    groupByCohorts: [],
    ...flowsReport,
  };

  const {anchors, filterByCohort, groupByCohorts} = flowsReport;
  const attrs = {
    dateRange: flowsReport.dateRange,
    events: [],
    groupBy: [],
    filters: (flowsReport.filters || []).map((filter) => new PropertyFilter(filter)),
    reverse: anchors.some((anchor) => anchor && anchor.reverse),
    ...omit(flowsReport, `anchors`, `filterByCohort`, `groupByCohorts`),
  };

  attrs.events = anchors.map((anchor) => {
    if (isEmpty(anchor)) {
      return null;
    }

    return {
      ...EMPTY_EVENT_ENTRY,
      ...pick(anchor, [`forward`, `reverse`]),
      event: {
        ...anchor.event,
      },
    };
  });

  if (!isEmpty(filterByCohort)) {
    const cohort = filterByCohort;
    attrs.filters = [
      ...attrs.filters,
      new PropertyFilter({
        filterOperator: cohort.negated ? ListOperator.DoesNotContain : ListOperator.Contains,
        filterValue: [
          {
            cohort: {
              id: cohort.id,
              negated: false,
            },
          },
        ],
        propertyType: PropertyType.List,
        resourceType: ResourceType.Cohort,
      }),
    ];
  } else if (!isEmpty(groupByCohorts)) {
    attrs.groupBy = [
      [
        ...groupByCohorts.map((cohort) => ({
          cohort: {
            ...cohort,
            negated: false,
          },
        })),
      ],
    ];
  }

  // prune out empty events
  if (isEmpty(attrs.events)) {
    delete attrs.events;
  }

  return attrs;
}

/**
 * TODO(pablo): this code will be deleted in favor a bookmark based API
 * Converts params of FlowsReport to flows API params
 */
export function convertFlowsReportToFlowsRequestParams(
  flowsReport: FlowsReport,
): Partial<AnchorsFlowsReportQueryParams> {
  const attrs: Partial<AnchorsFlowsReportQueryParams> = {};
  const anchors: AnchorsFlowsReportQueryParams['anchors'] = flowsReport.eventSelection.map((entry) => {
    const eventValue = entry.event.value;
    const isSessionEvent = SESSION_EVENTS[eventValue];

    return {
      ...pick(entry, `forward`, `reverse`),
      ...(isSessionEvent ? SESSION_EVENTS[eventValue] : {event: eventValue}),
    };
  });

  const {to: toDate, from: fromDate} = flowsReport.dateRange.getCalendarDates();

  if (flowsReport.cohortFilters.length) {
    const cohortFilter = flowsReport.cohortFilters[0];
    const cohortValue = cohortFilter.filterValue[0] as SavedCohortSegment;
    attrs.filter_by_cohort = {
      id: cohortValue.cohort.id,
      negated: cohortFilter.filterOperator === ListOperator.DoesNotContain,
    };
  } else {
    const groupByCohortSegment = flowsReport.groupBy.find(
      (segment) => getSegmentType(segment) === ResourceType.Cohort,
    ) as SavedCohortSegment[];
    if (groupByCohortSegment) {
      attrs.group_by_cohorts = groupByCohortSegment.map((segment) => ({
        id: segment.cohort.id,
        negated: false,
      }));
    }
  }

  const filters = [...flowsReport.eventFilters, ...flowsReport.userFilters];
  const serializedFilters = filters.map((filter) => new PropertyFilter(filter).getExpressionTree().serialize());
  if (serializedFilters.length === 1) {
    attrs.filter = serializedFilters[0];
  } else if (serializedFilters.length >= 1) {
    attrs.filter = {operator: LogicalOperator.And, children: serializedFilters};
  }

  if (flowsReport.timePercentiles.length) {
    attrs.time_percentiles = flowsReport.timePercentiles;
  }

  return {
    ...attrs,
    anchors,
    from_date: fromDate,
    to_date: toDate,
  };
}
